import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { CreateMeetingLinkFormValues } from '../CreateMeetingLinkForm'
import { StaffData } from '../StaffSelectionTable'

type WizardReviewStepProps = {
  staffs: StaffData[]
}

export default function WizardReviewStep(props: WizardReviewStepProps) {
  const { staffs } = props
  const { t } = useTranslation()
  const { values } = useFormikContext<CreateMeetingLinkFormValues>()

  const selectedStaffNames = staffs
    .filter((s) => values.staffs.includes(s.id))
    .map((s) => `${s.name} ${s.surname}`)

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('page.createMeetingLink.wizard.review.name')}
        </Typography>
        <Typography variant="h6">{values.name || '-'}</Typography>
      </Box>

      {values.description && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            {t('page.createMeetingLink.wizard.review.description')}
          </Typography>
          <Typography variant="body1">{values.description}</Typography>
        </Box>
      )}

      <Divider />

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('page.createMeetingLink.wizard.review.specialities')}
        </Typography>
        {values.specialities.length === 0 ? (
          <Typography variant="body2" color="text.secondary">-</Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {values.specialities.map((s) => (
              <Stack key={s.code} direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">{s.name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.price} €</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('page.createMeetingLink.wizard.review.staff')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
          {selectedStaffNames.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('page.createMeetingLink.wizard.review.currentUserOnly')}</Typography>
          ) : (
            selectedStaffNames.map((name) => (
              <Chip key={name} label={name} size="small" variant="outlined" />
            ))
          )}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" color="text.secondary">
          {t('page.createMeetingLink.wizard.review.options')}
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon fontSize="small" color={values.immediateConsultationConfig.enabled ? 'success' : 'disabled'} />
            <Typography variant="body2">
              {t('page.createMeetingLink.wizard.review.immediateConsultation')}:{' '}
              {values.immediateConsultationConfig.enabled
                ? t('page.createMeetingLink.wizard.review.enabled')
                : t('page.createMeetingLink.wizard.review.disabled')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon fontSize="small" color={values.busyEnabled ? 'success' : 'disabled'} />
            <Typography variant="body2">
              {t('page.createMeetingLink.wizard.review.busySlots')}:{' '}
              {values.busyEnabled
                ? t('page.createMeetingLink.wizard.review.enabled')
                : t('page.createMeetingLink.wizard.review.disabled')}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  )
}
